/* =====================================================================
 * Container numbers (ISO 6346).
 *
 * A container number is 4 letters then 7 digits: MSCU 123456 5. The last
 * digit is not free - it is calculated from the other ten. So a number
 * that was read wrongly can be CAUGHT, without needing to know what the
 * right answer was.
 *
 * That matters here because the number is read off a photo by OCR, and a
 * yard is the worst case for it: rust, mud, low sun, paint over paint,
 * photos taken at an angle. Before this, a bad read was saved silently
 * and only turned up later attached to the wrong container.
 *
 * Two things happen below:
 *
 *   1. Positions are known, so obvious OCR confusions can be corrected
 *      with certainty. The first four characters MUST be letters, so a
 *      "0" there can only be an O. The last seven MUST be digits, so an
 *      "O" there can only be a 0. This alone fixes most bad reads.
 *
 *   2. The check digit is recalculated. If it does not match, the read is
 *      wrong - full stop - and the user is told rather than the number
 *      being stored.
 * ===================================================================== */

/* Letter values from the standard. 11, 22 and 33 are skipped, which is
 * why the run is not simply A=10..Z=35. */
const LETTER_VALUES = {
  A: 10, B: 12, C: 13, D: 14, E: 15, F: 16, G: 17, H: 18, I: 19,
  J: 20, K: 21, L: 23, M: 24, N: 25, O: 26, P: 27, Q: 28, R: 29,
  S: 30, T: 31, U: 32, V: 34, W: 35, X: 36, Y: 37, Z: 38,
};

/* Characters OCR mixes up, and what each becomes once we know whether a
 * letter or a digit belongs in that position. */
const TO_LETTER = { "0": "O", "1": "I", "2": "Z", "5": "S", "6": "G", "8": "B" };
const TO_DIGIT  = { O: "0", Q: "0", D: "0", I: "1", L: "1", Z: "2", S: "5", B: "8", G: "6" };

/* Strip anything that is not a letter or digit, and upper-case it. */
export function clean(raw) {
  return String(raw || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
}

/* Apply the position rules: first four letters, rest digits. Returns the
 * corrected string plus what was changed, so the user can be shown it. */
export function autoCorrect(raw) {
  const s = clean(raw);
  const fixes = [];
  const out = s.split("").map((ch, i) => {
    if (i < 4) {
      if (TO_LETTER[ch]) { fixes.push(`${ch}→${TO_LETTER[ch]}`); return TO_LETTER[ch]; }
    } else if (TO_DIGIT[ch]) {
      fixes.push(`${ch}→${TO_DIGIT[ch]}`);
      return TO_DIGIT[ch];
    }
    return ch;
  }).join("");
  return { value: out, fixes };
}

/* The check digit for the first 10 characters.
 *
 * Each character's value is doubled by position - 1, 2, 4, 8 ... 512 -
 * the total is taken modulo 11, and a result of 10 is written as 0. */
export function checkDigit(firstTen) {
  const s = clean(firstTen).slice(0, 10);
  if (s.length !== 10) return null;
  let sum = 0;
  for (let i = 0; i < 10; i++) {
    const ch = s[i];
    const value = i < 4 ? LETTER_VALUES[ch] : (/[0-9]/.test(ch) ? Number(ch) : undefined);
    if (value === undefined) return null;       // not a container number at all
    sum += value * Math.pow(2, i);
  }
  return (sum % 11) % 10;
}

/* Check a full 11-character number.
 *
 * Returns what is wrong in words, because "invalid" on its own tells the
 * person holding the camera nothing useful. */
export function validate(raw) {
  const { value, fixes } = autoCorrect(raw);

  if (!value) return { ok: false, value, fixes, reason: "No number was read." };
  if (value.length !== 11) {
    return {
      ok: false, value, fixes,
      reason: `A container number is 11 characters. This has ${value.length}.`,
    };
  }
  if (!/^[A-Z]{4}[0-9]{7}$/.test(value)) {
    return {
      ok: false, value, fixes,
      reason: "It should be 4 letters then 7 numbers, like MSCU1234565.",
    };
  }
  if (!(value[3] === "U" || value[3] === "J" || value[3] === "Z")) {
    // Not fatal - plenty of real numbers in older records break this - so
    // it is reported as a warning, not a rejection.
    const expected = checkDigit(value);
    return {
      ok: expected === Number(value[10]),
      value, fixes,
      warning: `The 4th letter is usually U, J or Z. This one is ${value[3]}.`,
      reason: expected === Number(value[10]) ? null
        : `The check digit should be ${expected}, but the number ends in ${value[10]}.`,
    };
  }

  const expected = checkDigit(value);
  if (expected === null) return { ok: false, value, fixes, reason: "Could not read the number." };
  if (expected !== Number(value[10])) {
    return {
      ok: false, value, fixes,
      reason: `The check digit should be ${expected}, but the number ends in ${value[10]}. ` +
              "Something was misread - check the photo.",
    };
  }
  return { ok: true, value, fixes, reason: null };
}

/* Last resort: the check digit does not match, so ONE character was
 * probably misread. Try the swaps OCR actually makes, one at a time, and
 * see whether any single change produces a number that checks out.
 *
 * This only answers when exactly ONE candidate works. If two different
 * corrections both produce a valid number there is no way to know which
 * was on the container, and guessing would be worse than asking - so it
 * returns nothing and the person checks the photo.
 *
 * This is what rescues Q read as O: "O" is a legal letter in that
 * position, so the position rules cannot help, but only CSQU makes the
 * check digit come out right.
 *
 * IMPORTANT - this is a QUESTION, never an answer. It finds a number that
 * would check out, not the number that was on the container. Given
 * CSQU3054983, where a digit was genuinely misread, it happily proposes
 * GSQU3054983: one character changed, check digit satisfied, and wrong.
 * There is no way to tell the two situations apart from the string alone.
 *
 * So the caller must SHOW the suggestion and have a person confirm it
 * against the photo. Never write it in silently. */
const CONFUSIONS = {
  O: ["Q", "D", "C", "U"], Q: ["O"], D: ["O"], C: ["O", "G"], U: ["O", "V"],
  G: ["C", "6"], B: ["8"], S: ["5"], Z: ["2"], I: ["1", "L"], L: ["I", "1"],
  M: ["N"], N: ["M"], V: ["U", "Y"], Y: ["V"], T: ["I"], K: ["X"], X: ["K"],
  "0": ["8", "6"], "1": ["7"], "2": ["7"], "3": ["8"], "5": ["6", "8"],
  "6": ["5", "8", "0"], "7": ["1"], "8": ["0", "3", "6", "9"], "9": ["8"],
};

export function suggest(raw) {
  const { value } = autoCorrect(raw);
  if (value.length !== 11) return null;

  const hits = new Set();
  for (let i = 0; i < 11; i++) {
    const swaps = CONFUSIONS[value[i]] || [];
    for (const alt of swaps) {
      // Keep the shape: letters stay in the first four, digits after.
      const isLetterSlot = i < 4;
      if (isLetterSlot !== /[A-Z]/.test(alt)) continue;
      const candidate = value.slice(0, i) + alt + value.slice(i + 1);
      if (!/^[A-Z]{4}[0-9]{7}$/.test(candidate)) continue;
      if (checkDigit(candidate) === Number(candidate[10])) hits.add(candidate);
    }
  }
  // Only one possible answer is an answer. More than one is a guess.
  return hits.size === 1 ? Array.from(hits)[0] : null;
}

/* Format for display: MSCU 123456 5 */
export function pretty(raw) {
  const s = clean(raw);
  if (s.length !== 11) return s;
  return `${s.slice(0, 4)} ${s.slice(4, 10)} ${s.slice(10)}`;
}
